// pages/BulkUploadPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { Spinner } from "reactstrap";
import * as XLSX from "xlsx";
import {
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  Search,
  ChevronDown,
  X,
  FileDown,
  Upload,
  Info,
  AlertCircle,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import ExcelReuploadModal from "../components/ExcelReuploadModal";
import api from "../api";


const MANDATORY_SERVER_FIELDS = new Set([
  "email",
  "e_mail",
  "mail",
  "phone",
  "mobile",
  "cell",
  "contact",
  "tel",
  "ph_no",
  "phno",
  "amount",
  "fee",
  "fees",
]);

function normalizeHeader(str) {
  return String(str)
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[''`]/g, "")
    .replace(/[^a-z0-9\s_]/g, " ")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function isMandatoryField(normalizedHeader) {
  if (MANDATORY_SERVER_FIELDS.has(normalizedHeader)) return true;
  for (const m of MANDATORY_SERVER_FIELDS) {
    if (normalizedHeader.startsWith(m)) return true;
  }
  return false;
}

function parseExcelHeaders(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), {
          type: "array",
        });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        if (!rows.length) return reject(new Error("File is empty"));
        resolve((rows[0] || []).map((h) => String(h).trim()).filter(Boolean));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
}
const validateRowCount = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    showError("No data rows found in the uploaded file.");
    return false;
  }
  if (rows.length > 2000) {
    showError(`Maximum 2000 rows allowed. Your file has ${rows.length} rows.`);
    return false;
  }
  return true;
};
// Searchable Slug Dropdown
function SearchableSlugDropdown({ slugs, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = slugs.filter((s) =>
    s.toLowerCase().includes(query.toLowerCase()),
  );

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (slug) => {
    onChange(slug);
    setOpen(false);
    setQuery("");
  };

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <div className="bulk-slug-dropdown-container" ref={containerRef}>
      <div
        className={`bulk-slug-dropdown-trigger ${open ? "bulk-active" : ""} ${disabled ? "bulk-disabled" : ""}`}
        onClick={handleOpen}
      >
        <span className="bulk-slug-dropdown-value">
          <FileSpreadsheet size={15} color={value ? "#0C1B39" : "#adb5bd"} />
          {value || "Select a slug…"}
        </span>
        <div className="bulk-slug-dropdown-icons">
          {value && !disabled && (
            <span
              className="bulk-slug-clear-btn"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            >
              <X size={13} color="#6c757d" />
            </span>
          )}
          <ChevronDown
            size={16}
            color="#6c757d"
            className={`bulk-chevron ${open ? "bulk-rotated" : ""}`}
          />
        </div>
      </div>

      {open && (
        <div className="bulk-slug-dropdown-menu">
          <div className="bulk-slug-search-box">
            <Search size={14} color="#6c757d" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search slugs…"
            />
            {query && (
              <span className="bulk-clear-search" onClick={() => setQuery("")}>
                <X size={13} color="#adb5bd" />
              </span>
            )}
          </div>

          <div className="bulk-slug-list">
            {filtered.length === 0 ? (
              <div className="bulk-no-results">No slugs match "{query}"</div>
            ) : (
              filtered.map((slug) => (
                <div
                  key={slug}
                  className={`bulk-slug-item ${slug === value ? "bulk-selected" : ""}`}
                  onClick={() => handleSelect(slug)}
                >
                  <FileSpreadsheet
                    size={14}
                    color={slug === value ? "#0C1B39" : "#adb5bd"}
                  />
                  {slug}
                  {slug === value && (
                    <CheckCircle2
                      size={13}
                      color="#0C1B39"
                      className="bulk-selected-icon"
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Main Page
export default function BulkUploadPage() {
  const [slugs, setSlugs] = useState([]);
  const [formsMap, setFormsMap] = useState({});
  const [loadingSlugs, setLoadingSlugs] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [downloadMsg, setDownloadMsg] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  useEffect(() => {
    const fetchForms = async () => {
      try {
        const token = localStorage.getItem("jwt");

        const response = await api.post(
          "/forms/fetchAllForms?field=slug",
          {}, // empty body
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        const result = response.data;
        const map = {};
        result.forEach((f) => {
          if (f.slug) map[f.slug] = f;
        });
        setSlugs(result.map((f) => f.slug).filter(Boolean));
        setFormsMap(map);
      } catch (err) {
        console.error("Failed to fetch forms:", err);
      } finally {
        setLoadingSlugs(false);
      }
    };
    fetchForms();
  }, []);

  useEffect(() => {
    setDownloadStatus(null);
    setDownloadMsg("");
  }, [selectedSlug]);

  const selectedForm = selectedSlug ? formsMap[selectedSlug] : null;

  const handleDownloadTemplate = async () => {
    if (!selectedSlug || downloading) return;
    setDownloading(true);
    setDownloadStatus(null);
    setDownloadMsg("");
    try {
      const token = localStorage.getItem("jwt");

      const response = await api.post(
        `/bulk/users/export/template`,
        {}, // request body (empty if not needed)
        {
          params: {
            formId: formsMap[selectedSlug].formId,
            slug: selectedSlug,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: "blob", // ensures you get a file blob back
        },
      );

      const blob = response.data;
      let filename = `template_${selectedSlug}.xlsx`;
      const contentDisposition = response.headers["content-disposition"] || "";
      const match = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
      );
      if (match?.[1]) filename = match[1].replace(/['"]/g, "");

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      const file = new File([blob], filename, { type: blob.type });
      const localRawHeaders = await parseExcelHeaders(file);

      const res = await api.post(
        `/bulk/rowHeaders?slug=${selectedSlug}`,{},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = res.data;
      const srvRawHeaders = Array.isArray(data) ? data : (data?.headers ?? []);

      const normLocal = localRawHeaders.map(normalizeHeader);
      const normSrv = srvRawHeaders.map(normalizeHeader);

      const filteredNormSrv = normSrv.filter((h) => !isMandatoryField(h));
      const filteredSrvRaw = srvRawHeaders.filter(
        (_, i) => !isMandatoryField(normSrv[i]),
      );
      const filteredNormLocal = normLocal.filter((h) => !isMandatoryField(h));

      const missing = filteredSrvRaw.filter(
        (_, i) => !filteredNormLocal.includes(filteredNormSrv[i]),
      );
      const extra = filteredNormLocal.filter(
        (h) => !filteredNormSrv.includes(h),
      );

      if (!missing.length && !extra.length) {
        setDownloadStatus("ok");
        setDownloadMsg("Template downloaded successfully");
      } else {
        setDownloadStatus("warn");
        const parts = [];
        if (missing.length) parts.push(`Missing: ${missing.join(", ")}`);
        if (extra.length) parts.push(`Extra: ${extra.join(", ")}`);
        setDownloadMsg(`Column mismatch in template. ${parts.join(" | ")}`);
      }
    } catch (err) {
      setDownloadStatus("error");
      setDownloadMsg(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to download template.",
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="px-shell">
      <Sidebar />

      <main className="px-main">
        <div className="px-page-head mb-4">
          <h2 className="px-title">Bulk Upload</h2>
          <div className="px-sub">Download a form template and upload master data in bulk</div>
        </div>

        <div className="bulk-content-grid">
          {/* Slug Selector Card */}
          <div className="bulk-card bulk-slug-card">
            <div className="bulk-card-header">
              <label>Select Form Slug</label>
              <p>
                Choose the form you want to work with. You can search by slug
                name.
              </p>
            </div>

            {loadingSlugs ? (
              <div className="bulk-loading-state">
                <Spinner size="sm" color="primary" /> Loading slugs…
              </div>
            ) : (
              <SearchableSlugDropdown
                slugs={slugs}
                value={selectedSlug}
                onChange={setSelectedSlug}
              />
            )}

            {selectedForm && (
              <div className="bulk-form-meta">
                <div>
                  <strong>Title:</strong> {selectedForm.title || "—"}
                </div>
              </div>
            )}
          </div>

          {/* Actions Card */}
          <div className="bulk-card bulk-actions-card">
            <div className="bulk-card-header">
              <label>Actions</label>
              <p>
                Download the Excel template for this slug, fill it with data,
                then upload it back.
              </p>
            </div>

            {!selectedSlug ? (
              <div className="bulk-empty-state">
                <FileSpreadsheet size={28} />
                <p>Select a slug to enable actions</p>
              </div>
            ) : (
              <>
                <div className="bulk-action-buttons">
                  <button
                    className="bulk-btn bulk-btn-primary"
                    onClick={handleDownloadTemplate}
                    disabled={downloading}
                  >
                    {downloading ? (
                      <>
                        <Spinner size="sm" /> Downloading…
                      </>
                    ) : (
                      <>
                        <FileDown size={16} /> Download Template
                      </>
                    )}
                  </button>

                  <button
                    className="bulk-btn bulk-btn-outline"
                    onClick={() => setUploadModalOpen(true)}
                  >
                    <Upload size={16} /> Upload Excel
                  </button>
                </div>

                {downloadStatus && (
                  <div className={`bulk-download-status ${downloadStatus}`}>
                    {downloadStatus === "ok" && <CheckCircle2 size={16} />}
                    {downloadStatus === "warn" && <AlertTriangle size={16} />}
                    {downloadStatus === "error" && <AlertCircle size={16} />}
                    <span>{downloadMsg}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="bulk-notes-card">
          <div className="bulk-notes-header">
            <Info size={16} />
            <span>Note</span>
          </div>
          <ul className="bulk-notes-list">
            <li>
              <strong>Do not add any extra columns</strong> to the template
              Excel. The system validates headers strictly.
            </li>
            <li>
              <strong>Do not remove or rename existing columns.</strong>
            </li>
            <li>
              If you need new fields, create a new form with those fields.
            </li>
            <li>Only up to 2000 rows can be uploaded at once</li>
            
            <li>
              Supported formats: <strong>.xlsx</strong>, <strong>.xls</strong>,{" "}
              <strong>.csv</strong>.
            </li>
            
          </ul>
        </div>

        <ExcelReuploadModal
  isOpen={uploadModalOpen}
  toggle={() => setUploadModalOpen(false)}
  formId={selectedForm?.formId}
  slug={selectedSlug}
  onSuccess={(uploadedRows) => {
    if (validateRowCount(uploadedRows)) {
      setUploadModalOpen(false);   
    } else {
      // handle invalid row count if needed
    }
  }}
/>

      </main>
    </div>
  );
}
